import type { ResolutionDecision } from '@/features/SalesforceIngest/lib/describeResolution'
import {
  type PayloadParseResult,
  readResolutionFields,
  resolutionIdentity,
} from '@/features/SalesforceIngest/lib/parsePayloadBlock'
import { useEffect, useRef, useState } from 'react'

const NO_DECISIONS: ResolutionDecision = {
  createNewOrg: false,
  createNewAccount: false,
  orgId: '',
}

export type PastedDecisions = {
  decision: ResolutionDecision
  isPrefilled: boolean
  isPasteOpen: boolean
  prefillKey: number
  updateDecision: (patch: Partial<ResolutionDecision>) => void
  openPaste: () => void
  notePastedBlock: () => void
}

export function usePastedDecisions(
  parsed: PayloadParseResult,
  raw: string,
): PastedDecisions {
  const [isPasteOpen, setPasteOpen] = useState(true)
  const [isPrefilled, setPrefilled] = useState(false)
  const [decision, setDecision] = useState<ResolutionDecision>(NO_DECISIONS)
  const [prefillKey, setPrefillKey] = useState(0)

  const appliedIdentity = useRef<string | null>(null)
  const wasPrefilled = useRef(false)
  const isAutoCollapseSuppressed = useRef(false)
  const isReplacementPending = useRef(false)

  useEffect(() => {
    const isReplacement = isReplacementPending.current
    isReplacementPending.current = false

    if (!parsed.ok) {
      return
    }

    const fields = readResolutionFields(raw)
    const identity = resolutionIdentity(parsed.payload, fields)
    if (identity === appliedIdentity.current) {
      return
    }
    appliedIdentity.current = identity

    if (isReplacement || !isAutoCollapseSuppressed.current) {
      isAutoCollapseSuppressed.current = false
      setPasteOpen(false)
    }
    setPrefilled(fields !== null)
    if (fields) {
      setDecision(fields)
      setPrefillKey((key) => key + 1)
    } else if (wasPrefilled.current) {
      setDecision(NO_DECISIONS)
      setPrefillKey((key) => key + 1)
    }
    wasPrefilled.current = fields !== null
  }, [parsed, raw])

  return {
    decision,
    isPrefilled,
    isPasteOpen,
    prefillKey,
    updateDecision: (patch) =>
      setDecision((current) => ({ ...current, ...patch })),
    openPaste: () => {
      isAutoCollapseSuppressed.current = true
      setPasteOpen(true)
    },
    notePastedBlock: () => {
      isReplacementPending.current = true
    },
  }
}
